import type { Venue } from '../planning.ts'
import { isClientIncompatible } from '../planner-api.ts'
import { roadMatrix } from '../road/client.ts'
import type { ScheduledRoutedPerson } from './modes.ts'

type MatrixLoader = typeof roadMatrix

function isSuperseded(cause: unknown): boolean {
  return cause instanceof Error && cause.message.startsWith('Superseded')
}

export interface ScheduledWalkingEgressInput {
  participantIndex: number
  person: ScheduledRoutedPerson
  candidatesByVenue: number[][]
}

export interface ScheduledWalkingEgressFailure {
  inputs: ScheduledWalkingEgressInput[]
  cause: unknown
}

export async function loadScheduledWalkingEgress(
  inputs: ScheduledWalkingEgressInput[],
  venues: Venue[],
  loadMatrix: MatrixLoader = roadMatrix,
  assertCurrent: () => void = () => undefined,
): Promise<ScheduledWalkingEgressFailure[]> {
  const byBackend = new Map<ScheduledRoutedPerson['endpoints'], ScheduledWalkingEgressInput[]>()
  for (const input of inputs) {
    if (!input.candidatesByVenue.some((indexes) => indexes.length)) {
      input.person.walkingEgressByVenue = venues.map(() => [])
      continue
    }
    const group = byBackend.get(input.person.endpoints) ?? []
    group.push(input)
    byBackend.set(input.person.endpoints, group)
  }

  const settled = await Promise.all([...byBackend.values()].map(async (group) => {
    try {
      await loadGroup(group, venues, loadMatrix)
      return []
    } catch (cause) {
      if (isClientIncompatible(cause) || isSuperseded(cause)) throw cause
      assertCurrent()
      if (group.length === 1) return [{ inputs: group, cause }]
      return (await Promise.all(group.map(async (input) => {
        try {
          await loadGroup([input], venues, loadMatrix)
          return null
        } catch (fallbackCause) {
          if (isClientIncompatible(fallbackCause) || isSuperseded(fallbackCause)) throw fallbackCause
          assertCurrent()
          return { inputs: [input], cause: fallbackCause }
        }
      }))).filter((failure): failure is ScheduledWalkingEgressFailure => failure !== null)
    }
  }))
  return settled.flat()
}

async function loadGroup(
  group: ScheduledWalkingEgressInput[],
  venues: Venue[],
  loadMatrix: MatrixLoader,
): Promise<void> {
  const endpoints = group[0].person.endpoints
  const endpointIndexes = [...new Set(group.flatMap(({ candidatesByVenue }) =>
    candidatesByVenue.flat()))]
  const matrix = await loadMatrix(
    'foot',
    endpointIndexes.map((endpointIndex) => {
      const endpoint = endpoints[endpointIndex]
      return {
        displayName: endpoint[1], lat: endpoint[2], lon: endpoint[3], type: 'transit-stop',
      }
    }),
    venues,
  )
  const rows = new Map(endpointIndexes.map((endpointIndex, row) => [endpointIndex, row]))
  for (const { person, candidatesByVenue } of group) {
    person.walkingEgressByVenue = candidatesByVenue.map((indexes, venueIndex) =>
      indexes.flatMap((endpointIndex) => {
        const seconds = matrix[rows.get(endpointIndex)!][venueIndex]
        return seconds === null ? [] : [{ endpointIndex, seconds }]
      }))
  }
}
